import { SuppressionSiteServerResponse } from '../app-state/thunks/getSuppressionContent';
import {
  EmailResponse,
  SiteServer,
  WriteToWithdrawEmailRequest
} from '../components/common/CommonTypes';
import { ApiHelper } from './apiHelper';

/**
 * Client-side API wrapper for site bootstrap and login-attempt endpoints.
 */
export class SiteApi {
  /**
   * Loads site bootstrap details (brand/content/user/config flags).
   *
   * @param journeyType Journey type identifier.
   * @param instanceId Journey instance identifier.
   * @param successCallback Invoked with site payload on success.
   * @param failureCallback Invoked with an error message on failure.
   * @param signal Abort signal used to cancel the request.
   */
  async getSiteDetails(
    journeyType: number,
    instanceId: string,
    successCallback: (site: SiteServer) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): Promise<void> {
    const dataUrl = `/api/site/site?instanceid=${instanceId}&journeytype=${journeyType}&pathname=${window.location.pathname}&browserLanguage=${navigator.language}`;

    await new ApiHelper().getDataAsync(
      dataUrl,
      successCallback,
      failureCallback,
      signal
    );
  }

  async getSuppressionPageDetails(
    country = '',
    successCallback: (site: SuppressionSiteServerResponse) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): Promise<void> {
    const dataUrl = `/api/site/suppression?country=${country}&browserLanguage=${navigator.language}`;

    await new ApiHelper().getDataAsync(
      dataUrl,
      successCallback,
      failureCallback,
      signal
    );
  }

  /**
   * Validates a captcha token.
   *
   * @param token Recaptcha token.
   * @param successCallback Invoked with a boolean result on success.
   * @param failureCallback Invoked when the request fails.
   * @param signal Abort signal used to cancel the request.
   */
  async validateCaptcha(
    token: string,
    successCallback: (hasAccess: boolean) => void,
    failureCallback: () => void,
    signal: AbortSignal
  ): Promise<void> {
    const dataUrl = `api/Site/validate-captcha`;
    const data = { token: token };
    new ApiHelper().postDataAsync(
      dataUrl,
      data,
      successCallback,
      failureCallback,
      signal
    );
  }

  refreshToken(
    successCallback: (response: {
      token: string;
      tokenExpiresOn: Date;
    }) => void,
    failureCallback: () => void,
    signal: AbortSignal
  ): void {
    const url = '/api/site/refreshtoken';

    new ApiHelper().getDataAsync(url, successCallback, failureCallback, signal);
  }

  sendWriteToWithdrawEmail(
    writeToWithdrawEmailRequest: WriteToWithdrawEmailRequest,
    successCallback: (response: EmailResponse) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const url = '/api/site/sendwritetowithdrawemail';

    new ApiHelper().postDataAsync(
      url,
      writeToWithdrawEmailRequest,
      successCallback,
      failureCallback,
      signal
    );
  }
}
